import { z } from "zod";
const PortableKeySchema = z.string().regex(/^[a-z][a-z0-9_]{0,63}$/u);
const HexColorSchema = z.string().regex(/^#[0-9A-F]{6}$/u);
export const ScheduleSlotStateSchema = z.enum([
    "unavailable",
    "free",
    "occupied",
    "reserved",
    "day_off",
]);
export const ScheduleColorRuleSchema = z.object({
    state: ScheduleSlotStateSchema,
    colors: z.array(HexColorSchema).min(1).max(20),
}).strict();
export const ScheduleManagerBindingSchema = z.object({
    rowLabel: z.string().trim().min(1).max(200),
    crmUserId: z.string().uuid(),
    enabled: z.boolean(),
    selectionWeight: z.number().int().min(1).max(100).default(1),
}).strict();
export const ScheduleAfterBookingSchema = z.object({
    targetStageId: z.string().uuid().nullable(),
    assignSelectedManager: z.boolean(),
    appointmentDateFieldId: z.number().int().positive()
        .max(Number.MAX_SAFE_INTEGER).nullable().default(null),
    task: z.object({
        taskTypeId: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
        text: z.string().trim().min(1).max(1_000),
    }).strict().nullable(),
    reminders: z.array(z.object({
        offsetMinutesBefore: z.number().int().min(0).max(43_200),
        message: z.string().trim().min(1).max(4_000),
    }).strict()).max(10),
}).strict().superRefine((configuration, context) => {
    if (configuration.targetStageId === null
        && !configuration.assignSelectedManager
        && configuration.appointmentDateFieldId === null
        && configuration.task === null
        && configuration.reminders.length === 0) {
        context.addIssue({
            code: "custom",
            message: "After-booking automation must contain at least one action.",
        });
    }
    const offsets = new Set();
    configuration.reminders.forEach((reminder, index) => {
        if (offsets.has(reminder.offsetMinutesBefore)) {
            context.addIssue({
                code: "custom",
                path: ["reminders", index, "offsetMinutesBefore"],
                message: "A reminder offset cannot be duplicated.",
            });
        }
        offsets.add(reminder.offsetMinutesBefore);
    });
});
export const GoogleSheetsScheduleBindingSchema = z.object({
    schemaVersion: z.literal("google_sheets_schedule_v1"),
    key: PortableKeySchema,
    displayName: z.string().trim().min(2).max(200),
    spreadsheetId: z.string().trim().regex(/^[A-Za-z0-9_-]{20,200}$/u),
    sheetName: z.string().trim().min(1).max(200),
    technicalLedgerSheetName: z.string().trim().min(1).max(200),
    timezone: z.string().trim().min(3).max(100),
    dateYear: z.number().int().min(2020).max(2200),
    scanRange: z.string().trim().min(3).max(200),
    layout: z.object({
        dateMarker: z.string().trim().min(1).max(50),
        timeMarker: z.string().trim().min(1).max(50),
        managerColumn: z.number().int().min(0).max(100),
        bookingNotePrefix: z.string().trim().min(3).max(100),
    }).strict(),
    colorRules: z.array(ScheduleColorRuleSchema).length(5),
    managers: z.array(ScheduleManagerBindingSchema).min(1).max(100),
    bookingRules: z.object({
        minimumLeadMinutes: z.number().int().min(0).max(10_080),
        slotDurationMinutes: z.number().int().min(5).max(1_440),
        selectionStrategy: z.enum([
            "earliest",
            "least_booked_then_earliest",
            "weighted_round_robin",
        ]),
    }).strict(),
    afterBooking: ScheduleAfterBookingSchema.nullable().default(null),
}).strict().superRefine((binding, context) => {
    const states = new Set(binding.colorRules.map((rule) => rule.state));
    if (states.size !== ScheduleSlotStateSchema.options.length) {
        context.addIssue({
            code: "custom",
            path: ["colorRules"],
            message: "Every schedule slot state must have exactly one color rule.",
        });
    }
    const colors = new Set();
    binding.colorRules.forEach((rule, ruleIndex) => {
        rule.colors.forEach((color, colorIndex) => {
            if (colors.has(color)) {
                context.addIssue({
                    code: "custom",
                    path: ["colorRules", ruleIndex, "colors", colorIndex],
                    message: "One schedule color cannot represent more than one state.",
                });
            }
            colors.add(color);
        });
    });
    const managerLabels = new Set();
    const crmUsers = new Set();
    binding.managers.forEach((manager, index) => {
        const normalizedLabel = normalizeScheduleLabel(manager.rowLabel);
        if (managerLabels.has(normalizedLabel)) {
            context.addIssue({
                code: "custom",
                path: ["managers", index, "rowLabel"],
                message: "A schedule row can be bound only once.",
            });
        }
        if (crmUsers.has(manager.crmUserId)) {
            context.addIssue({
                code: "custom",
                path: ["managers", index, "crmUserId"],
                message: "A CRM manager can be bound to only one schedule row.",
            });
        }
        managerLabels.add(normalizedLabel);
        crmUsers.add(manager.crmUserId);
    });
});
export const ScheduleDiscoveryConfidenceSchema = z.enum([
    "exact",
    "unique_first_name",
    "unresolved",
]);
export const VerifiedConnectorResourceSchema = z.object({
    kind: z.literal("spreadsheet"),
    externalId: z.string().trim().regex(/^[A-Za-z0-9_-]{20,200}$/u),
    displayName: z.string().trim().min(1).max(500),
    verifiedAt: z.string().datetime(),
}).strict();
export const GoogleSheetsScheduleAnalysisSchema = z.object({
    source: VerifiedConnectorResourceSchema.extend({
        sheetName: z.string().trim().min(1).max(200),
        timezone: z.string().trim().min(3).max(100),
        analyzedRange: z.string().trim().min(3).max(200),
    }).strict(),
    dateBlockCount: z.number().int().positive().max(10_000),
    detectedLayout: z.object({
        dateYear: z.number().int().min(2020).max(2200),
        dateMarker: z.string().trim().min(1).max(50),
        timeMarker: z.string().trim().min(1).max(50),
        managerColumn: z.number().int().min(0).max(100),
    }).strict(),
    managerMappings: z.array(z.object({
        rowLabel: z.string().trim().min(1).max(200),
        crmUserId: z.string().uuid().nullable(),
        crmUserName: z.string().trim().min(1).max(200).nullable(),
        confidence: ScheduleDiscoveryConfidenceSchema,
    }).strict()).min(1).max(100),
    colorMappings: z.array(z.object({
        state: ScheduleSlotStateSchema,
        colors: z.array(HexColorSchema).min(1).max(20),
    }).strict()).length(5),
    proposedBinding: GoogleSheetsScheduleBindingSchema.nullable(),
    notices: z.array(z.string().trim().min(1).max(500)).max(20),
}).strict();
export const ScheduleBindingDraftMutationSchema = z.object({
    bindingId: z.string().uuid().nullable(),
    expectedRevision: z.number().int().nonnegative(),
    connectorAccountId: z.string().uuid(),
    snapshot: GoogleSheetsScheduleBindingSchema,
}).strict().superRefine((mutation, context) => {
    if ((mutation.bindingId === null && mutation.expectedRevision !== 0)
        || (mutation.bindingId !== null && mutation.expectedRevision === 0)) {
        context.addIssue({
            code: "custom",
            path: ["expectedRevision"],
            message: "A new binding starts at revision 0; an existing binding requires its current revision.",
        });
    }
});
export const ScheduleBindingDraftResultSchema = z.object({
    bindingId: z.string().uuid(),
    revision: z.number().int().positive(),
    updatedAt: z.string().datetime(),
}).strict();
export const ScheduleBindingPublicationMutationSchema = z.object({
    expectedDraftRevision: z.number().int().positive(),
    reason: z.string().trim().min(5).max(1_000),
    idempotencyKey: z.string().trim().min(16).max(160),
}).strict();
export const ScheduleBindingPublicationResultSchema = z.object({
    bindingId: z.string().uuid(),
    bindingVersionId: z.string().uuid(),
    version: z.number().int().positive(),
    contentHash: z.string().regex(/^[0-9a-f]{64}$/u),
    publishedAt: z.string().datetime(),
    replayed: z.boolean(),
}).strict();
export const ScheduleBindingActivationMutationSchema = z.object({
    bindingVersionId: z.string().uuid(),
    reason: z.string().trim().min(5).max(1_000),
    idempotencyKey: z.string().trim().min(16).max(160),
}).strict();
export const ScheduleBindingActivationResultSchema = z.object({
    bindingId: z.string().uuid(),
    bindingVersionId: z.string().uuid(),
    activationId: z.string().uuid(),
    activatedAt: z.string().datetime(),
    replayed: z.boolean(),
}).strict();
export const WorkspaceScheduleBindingSchema = z.object({
    bindingId: z.string().uuid(),
    connectorAccountId: z.string().uuid(),
    draftRevision: z.number().int().positive(),
    draftUpdatedAt: z.string().datetime(),
    snapshot: GoogleSheetsScheduleBindingSchema,
    latestPublishedVersion: z.object({
        bindingVersionId: z.string().uuid(),
        version: z.number().int().positive(),
        contentHash: z.string().regex(/^[0-9a-f]{64}$/u),
        publishedAt: z.string().datetime(),
    }).strict().nullable(),
    active: z.object({
        activationId: z.string().uuid(),
        bindingVersionId: z.string().uuid(),
        activatedAt: z.string().datetime(),
    }).strict().nullable(),
}).strict();
export const ScheduleWorkspaceSchema = z.object({
    connectorAccounts: z.array(z.object({
        id: z.string().uuid(),
        label: z.string().trim().min(1).max(200),
        status: z.enum(["not_configured", "checking", "ready", "degraded", "error"]),
        readyForScheduling: z.boolean(),
        selectedResource: VerifiedConnectorResourceSchema.nullable().default(null),
    }).strict()).max(20),
    crmManagers: z.array(z.object({
        id: z.string().uuid(),
        name: z.string().trim().min(1).max(200),
    }).strict()).max(1_000),
    crmStages: z.array(z.object({
        id: z.string().uuid(),
        name: z.string().trim().min(1).max(500),
        pipelineName: z.string().trim().min(1).max(500),
    }).strict()).max(5_000),
    bindings: z.array(WorkspaceScheduleBindingSchema).max(100),
}).strict();
export function normalizeScheduleLabel(value) {
    return value.trim().replace(/\s+/gu, " ").toLocaleLowerCase("ru-RU");
}
//# sourceMappingURL=scheduling.js.map